import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/apiService';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line, Pie, Bar } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    BarElement,
    ArcElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

const CHART_COLORS = [
    '#6366F1', // Indigo
    '#22C55E', // Green
    '#F97316', // Orange
    '#EC4899', // Pink
    '#0EA5E9', // Sky
    '#F59E0B', // Amber
    '#8B5CF6', // Purple
    '#14B8A6', // Teal
    '#EF4444', // Red
    '#10B981', // Emerald
];

const formatPercentage = (value) => Math.round(value * 1000) / 10;

const aggregateDistributionByLabel = (items, labelResolver) => {
    const buckets = new Map();

    (items ?? []).forEach((item) => {
        const resolvedLabel = labelResolver(item);
        const label = (typeof resolvedLabel === 'string' ? resolvedLabel.trim() : '') || '未設定';
        const count = Number(item?.count ?? 0);

        if (buckets.has(label)) {
            buckets.get(label).count += count;
            return;
        }

        buckets.set(label, { label, count });
    });

    const aggregated = Array.from(buckets.values());
    const total = aggregated.reduce((sum, item) => sum + (item.count ?? 0), 0);

    return aggregated.map((item) => ({
        ...item,
        percentage: total > 0 ? formatPercentage(item.count / total) : 0
    }));
};

const aggregateAttritionByLabel = (items, labelResolver) => {
    const buckets = new Map();

    (items ?? []).forEach((item) => {
        const resolvedLabel = labelResolver(item);
        const label = (typeof resolvedLabel === 'string' ? resolvedLabel.trim() : '') || '未設定';
        const resignedCount = Number(item?.resignedCount ?? 0);
        const totalCount = Number(item?.totalCount ?? 0);

        if (buckets.has(label)) {
            const bucket = buckets.get(label);
            bucket.resignedCount += resignedCount;
            bucket.totalCount += totalCount;
            return;
        }

        buckets.set(label, { label, resignedCount, totalCount });
    });

    return Array.from(buckets.values())
        .map((item) => ({
            ...item,
            attritionRate: item.totalCount > 0 ? formatPercentage(item.resignedCount / item.totalCount) : 0
        }))
        .sort((a, b) => {
            if (b.attritionRate !== a.attritionRate) return b.attritionRate - a.attritionRate;
            if (b.resignedCount !== a.resignedCount) return b.resignedCount - a.resignedCount;
            return b.totalCount - a.totalCount;
        });
};

const toJapaneseNationality = (nationality) => {
    if (!nationality) return '未設定';
    const trimmed = nationality.trim();
    const map = {
        '中国': '中国',
        'China': '中国',
        '韓国': '韓国',
        '韩国': '韓国',
        'Korea': '韓国',
        '缅甸': 'ミャンマー',
        'ミャンマー': 'ミャンマー',
        'Myanmar': 'ミャンマー',
        '印度尼西亚': 'インドネシア',
        'インドネシア': 'インドネシア',
        'Indonesia': 'インドネシア',
        '越南': 'ベトナム',
        'ベトナム': 'ベトナム',
        'Vietnam': 'ベトナム',
        '台湾': '台湾',
        'Taiwan': '台湾',
        '日本': '日本',
        'Japan': '日本',
        '菲律宾': 'フィリピン',
        'フィリピン': 'フィリピン',
        'Philippines': 'フィリピン',
        '泰国': 'タイ',
        'タイ': 'タイ',
        'Thailand': 'タイ',
        'ラオス': 'ラオス',
        '老挝': 'ラオス'
    };
    return map[trimmed] || trimmed;
};

const toJapaneseField = (field) => {
    if (!field) return '未設定';
    const trimmed = field.trim();
    const map = {
        '外食': '外食',
        '飲食料品製造': '飲食料品製造',
        '饮食料品製造': '飲食料品製造',
        '飲食製造': '飲食料品製造',
        '溶接': '溶接',
        '介護': '介護',
        '介护': '介護',
        '介護福祉': '介護',
        '農業': '農業',
        '农业': '農業',
        '建設': '建設',
        '建设': '建設',
        'ビルメンテナンス': 'ビルメンテナンス',
        '未設定': '未設定',
        '未设置': '未設定'
    };
    return map[trimmed] || trimmed;
};

export default function AdminAnalytics({ onInitialCostTalentCountClick }) {
    const { t } = useTranslation();
    const [analytics, setAnalytics] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedSalesStaffId, setSelectedSalesStaffId] = useState('all');
    const [selectedAttritionSalesStaffId, setSelectedAttritionSalesStaffId] = useState('all');
    const [managementFeeView, setManagementFeeView] = useState('current');
    const [selectedInitialCostSalesStaffId, setSelectedInitialCostSalesStaffId] = useState('all');
    const [syncInitialCostFilters, setSyncInitialCostFilters] = useState(false);
    const initialDateFilter = { startMonth: '', endMonth: '' };
    const [dateFilters, setDateFilters] = useState(initialDateFilter);
    const [pendingDateFilters, setPendingDateFilters] = useState(initialDateFilter);
    const [dateValidationError, setDateValidationError] = useState(null);
    const [initialCostDateFilters, setInitialCostDateFilters] = useState(initialDateFilter);
    const [pendingInitialCostDateFilters, setPendingInitialCostDateFilters] = useState(initialDateFilter);
    const [initialCostDateValidationError, setInitialCostDateValidationError] = useState(null);
    const [attritionDateFilters, setAttritionDateFilters] = useState(initialDateFilter);
    const [pendingAttritionDateFilters, setPendingAttritionDateFilters] = useState(initialDateFilter);
    const [attritionDateValidationError, setAttritionDateValidationError] = useState(null);

    // 图表选项状态（从localStorage读取）
    const STORAGE_KEY = 'admin_analytics_chart_preferences';
    const getStoredPreferences = () => {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            return stored ? JSON.parse(stored) : {
                lineChart: 'talent_count',    // 折线图：人才数量趋势
                pieChart: 'nationality',       // 饼图：国籍分布
                barChart1: 'new_talents',      // 柱状图1：月度新增
                barChart2: 'quarterly'         // 柱状图2：季度对比
            };
        } catch {
            return {
                lineChart: 'talent_count',
                pieChart: 'nationality',
                barChart1: 'new_talents',
                barChart2: 'quarterly'
            };
        }
    };

    const [chartOptions, setChartOptions] = useState(getStoredPreferences());

    // 保存图表选项到localStorage
    const updateChartOption = (chartType, value) => {
        const newOptions = { ...chartOptions, [chartType]: value };
        setChartOptions(newOptions);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(newOptions));
    };

    useEffect(() => {
        let isMounted = true;
        const loadAnalytics = async () => {
            setIsLoading(true);
            try {
                const data = await apiService.getAdminAnalytics({
                    startMonth: dateFilters.startMonth || undefined,
                    endMonth: dateFilters.endMonth || undefined,
                    initialCostStartMonth: (syncInitialCostFilters ? dateFilters.startMonth : initialCostDateFilters.startMonth) || undefined,
                    initialCostEndMonth: (syncInitialCostFilters ? dateFilters.endMonth : initialCostDateFilters.endMonth) || undefined,
                    attritionStartMonth: attritionDateFilters.startMonth || undefined,
                    attritionEndMonth: attritionDateFilters.endMonth || undefined,
                    attritionSalesStaffId: selectedAttritionSalesStaffId !== 'all' ? selectedAttritionSalesStaffId : undefined
                });
                if (!isMounted) return;
                setAnalytics(data);
                setError(null);
            } catch (err) {
                if (!isMounted) return;
                console.error('Failed to load admin analytics', err);
                setError(err?.message || t('adminAnalytics.loadError'));
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadAnalytics();

        return () => {
            isMounted = false;
        };
    }, [
        t,
        dateFilters.startMonth,
        dateFilters.endMonth,
        syncInitialCostFilters,
        initialCostDateFilters.startMonth,
        initialCostDateFilters.endMonth,
        attritionDateFilters.startMonth,
        attritionDateFilters.endMonth,
        selectedAttritionSalesStaffId
    ]);

    useEffect(() => {
        setSelectedSalesStaffId('all');
        setSelectedInitialCostSalesStaffId('all');
    }, [analytics]);

    if (isLoading) {
        return (
            <div className="space-y-6">
                <div className="bg-white rounded-xl shadow-lg p-6 animate-pulse">
                    <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
                    <div className="h-64 bg-slate-200 rounded"></div>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-white rounded-xl shadow-lg p-6 border border-red-200">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('adminAnalytics.title')}</h3>
                <p className="text-sm text-red-600">{t('adminAnalytics.errorOccurred')}: {error}</p>
            </div>
        );
    }

    if (!analytics) {
        return null;
    }

    const salesStaffRanking = (analytics.salesStaffRanking ?? []).slice().sort((a, b) => {
        const employedDiff = (b.employedTalents || 0) - (a.employedTalents || 0);
        if (employedDiff !== 0) return employedDiff;

        const pipelineA = (a.pendingTalents || 0) + (a.applyingTalents || 0);
        const pipelineB = (b.pendingTalents || 0) + (b.applyingTalents || 0);
        const pipelineDiff = pipelineB - pipelineA;
        if (pipelineDiff !== 0) return pipelineDiff;

        return (b.totalTalents || 0) - (a.totalTalents || 0);
    });

    const getSalesStaffOptionValue = (item) => (item.salesStaffId ?? 'unassigned').toString();
    const salesStaffRankingById = new Map(
        salesStaffRanking
            .filter((item) => item?.salesStaffId !== undefined && item?.salesStaffId !== null)
            .map((item) => [item.salesStaffId.toString(), item])
    );
    const salesStaffRankingByName = new Map(
        salesStaffRanking
            .filter((item) => item?.salesStaff)
            .map((item) => [item.salesStaff, item])
    );
    const getCurrentEmployedTalentCount = (item) => {
        if (!item) return 0;

        const rankingEntry = salesStaffRankingById.get(getSalesStaffOptionValue(item))
            ?? salesStaffRankingByName.get(item.salesStaff);

        return rankingEntry?.employedTalents ?? item.activeTalentCount ?? item.talentCount ?? 0;
    };

    const managementFeeStatsRaw = analytics.managementFeeStats ?? {};
    const managementFeeBySalesStaff = managementFeeStatsRaw.bySalesStaff ?? [];
    const managementFeeViews = managementFeeStatsRaw.views ?? {};
    const overallManagementFee = {
        totalMonthlyFee: managementFeeStatsRaw.totalMonthlyFee ?? 0,
        averageFeePerTalent: managementFeeStatsRaw.averageFeePerTalent ?? 0,
        medianFee: managementFeeStatsRaw.medianFee ?? 0,
        maxFee: managementFeeStatsRaw.maxFee ?? 0,
        minFee: managementFeeStatsRaw.minFee ?? 0,
        projectedAnnualRevenue: managementFeeStatsRaw.projectedAnnualRevenue ?? 0,
        firstMonthFeeTotal: managementFeeStatsRaw.firstMonthFeeTotal ?? 0,
        firstMonthFeeCount: managementFeeStatsRaw.firstMonthFeeCount ?? 0,
        talentsWithFee: managementFeeStatsRaw.talentsWithFee ?? 0,
        noChargeTalents: managementFeeStatsRaw.noChargeTalents ?? 0
    };

    const selectedSalesStaff = managementFeeBySalesStaff.find(item => getSalesStaffOptionValue(item) === selectedSalesStaffId);

    const viewLabels = {
        current: t('adminAnalytics.managementFeeViewOption.current'),
        rangeActual: t('adminAnalytics.managementFeeViewOption.rangeActual'),
        rangeForecast: t('adminAnalytics.managementFeeViewOption.rangeForecast'),
        rangeCombined: t('adminAnalytics.managementFeeViewOption.rangeCombined')
    };

    const emptyManagementFeeView = {
        startMonth: '',
        endMonth: '',
        totalFee: 0,
        averageFeePerTalent: 0,
        medianFee: 0,
        maxFee: 0,
        minFee: 0,
        talentsWithFee: 0,
        targetTalentsCount: 0,
        noChargeTalents: 0,
        billedMonths: 0,
        pausedMonths: 0
    };

    const overallManagementView = managementFeeViews[managementFeeView] ?? emptyManagementFeeView;
    const selectedManagementView = selectedSalesStaff?.views?.[managementFeeView] ?? emptyManagementFeeView;
    const currentManagementFee = selectedSalesStaffId === 'all' || !selectedSalesStaff
        ? overallManagementFee
        : selectedSalesStaff;
    const currentManagementFeeView = selectedSalesStaffId === 'all' || !selectedSalesStaff
        ? overallManagementView
        : selectedManagementView;

    const overallEmployedTalentCount = salesStaffRanking.length > 0
        ? salesStaffRanking.reduce((sum, staff) => sum + (staff.employedTalents ?? 0), 0)
        : (managementFeeStatsRaw.activeTalentsCount ?? managementFeeBySalesStaff.reduce((sum, item) => sum + (item.activeTalentCount ?? item.rangeTalentsWithFee ?? item.talentCount ?? 0), 0));

    const currentActiveTalentCount = selectedSalesStaffId === 'all' || !selectedSalesStaff
        ? overallEmployedTalentCount
        : getCurrentEmployedTalentCount(selectedSalesStaff);

    const currentMonthFeeTalentCount = selectedSalesStaffId === 'all' || !selectedSalesStaff
        ? (managementFeeStatsRaw.currentMonth?.talentsWithFee ?? overallManagementFee.talentsWithFee ?? managementFeeBySalesStaff.reduce((sum, item) => sum + (item.talentCount ?? 0), 0))
        : selectedSalesStaff.talentCount ?? 0;

    const currentViewFeeTalentCount = currentManagementFeeView.talentsWithFee ?? 0;
    const currentNoChargeTalentCount = selectedSalesStaffId === 'all' || !selectedSalesStaff
        ? (currentManagementFeeView.noChargeTalents ?? overallManagementFee.noChargeTalents ?? 0)
        : (selectedManagementView.noChargeTalents ?? selectedSalesStaff.noChargeTalents ?? 0);
    const currentFeeCountLabel = managementFeeView === 'current'
        ? t('adminAnalytics.managementFeeCurrentMonthCount', { count: currentMonthFeeTalentCount })
        : t('adminAnalytics.managementFeeViewFeeCount', {
            view: viewLabels[managementFeeView] ?? t('adminAnalytics.managementFeeViewOption.current'),
            count: currentViewFeeTalentCount
        });
    const currentTargetTalentCount = currentManagementFeeView.targetTalentsCount ?? currentManagementFeeView.talentsWithFee ?? 0;
    const currentTalentCount = managementFeeView === 'current' ? currentActiveTalentCount : currentTargetTalentCount;
    const currentNonBilledTalentCount = Math.max(
        0,
        currentTalentCount - (managementFeeView === 'current' ? currentMonthFeeTalentCount : currentViewFeeTalentCount) - currentNoChargeTalentCount
    );
    const managementFeeCurrentViewLabelKey = managementFeeView === 'current'
        ? 'adminAnalytics.managementFeeCurrentViewActive'
        : 'adminAnalytics.managementFeeCurrentView';

    const currentSalesStaffLabel = selectedSalesStaffId === 'all' || !selectedSalesStaff
        ? t('adminAnalytics.allSalesStaff')
        : selectedSalesStaff.salesStaff;

    const managementFeeTotalLabel = managementFeeView === 'current'
        ? t('adminAnalytics.totalMonthlyFee')
        : t('adminAnalytics.totalRangeFee');
    const isRangeView = managementFeeView !== 'current';

    const initialCostStatsRaw = analytics.initialCostStats ?? {};
    const initialCostBySalesStaff = initialCostStatsRaw.bySalesStaff ?? [];
    const overallInitialCost = {
        totalInitialCost: initialCostStatsRaw.totalInitialCost ?? 0,
        averageInitialCost: initialCostStatsRaw.averageInitialCost ?? 0,
        medianInitialCost: initialCostStatsRaw.medianInitialCost ?? 0,
        maxCost: initialCostStatsRaw.maxCost ?? 0,
        minCost: initialCostStatsRaw.minCost ?? 0,
        talentsWithCost: initialCostStatsRaw.talentsWithCost ?? 0,
        coverageRate: initialCostStatsRaw.coverageRate ?? 0,
        noChargeTalents: initialCostStatsRaw.noChargeTalents ?? 0,
        chargeablePopulation: initialCostStatsRaw.chargeablePopulation ?? 0
    };

    const initialCostSelectedId = syncInitialCostFilters ? selectedSalesStaffId : selectedInitialCostSalesStaffId;
    const selectedInitialCost = initialCostBySalesStaff.find(item => getSalesStaffOptionValue(item) === initialCostSelectedId);

    const fallbackInitialCost = {
        talentCount: 0,
        totalInitialCost: 0,
        averageInitialCost: 0,
        medianInitialCost: 0,
        maxCost: 0,
        minCost: 0,
        coverageRate: 0,
        noChargeTalents: 0,
        chargeablePopulation: 0
    };

    const currentInitialCost = initialCostSelectedId === 'all'
        ? overallInitialCost
        : (selectedInitialCost ?? fallbackInitialCost);

    const currentInitialCostCount = initialCostSelectedId === 'all'
        ? (overallInitialCost.talentsWithCost ?? initialCostBySalesStaff.reduce((sum, item) => sum + (item.talentCount ?? 0), 0))
        : (selectedInitialCost?.talentCount ?? 0);

    const currentInitialCostLabel = initialCostSelectedId === 'all'
        ? t('adminAnalytics.allSalesStaff')
        : (syncInitialCostFilters ? currentSalesStaffLabel : (selectedInitialCost?.salesStaff ?? t('adminAnalytics.allSalesStaff')));

    const specifiedSkill2Stats = analytics.specifiedSkill2Stats ?? {
        totalCount: 0,
        totalIntroductionFee: 0,
        averageIntroductionFee: 0,
        unregisteredFeeCount: 0,
        bySalesStaff: []
    };

    const translatedNationalityDistribution = aggregateDistributionByLabel(
        analytics.nationalityDistribution,
        (item) => toJapaneseNationality(item?.nationality)
    );

    const translatedFieldDistribution = aggregateDistributionByLabel(
        analytics.fieldDistribution,
        (item) => toJapaneseField(item?.field)
    );

    const nationalityTotal = translatedNationalityDistribution.reduce((sum, item) => sum + (item.count ?? 0), 0);
    const attritionStats = analytics.attritionStats ?? {};

    const attritionByNationality = aggregateAttritionByLabel(
        attritionStats.byNationality,
        (item) => toJapaneseNationality(item?.nationality)
    );
    const attritionByField = aggregateAttritionByLabel(
        attritionStats.byField,
        (item) => toJapaneseField(item?.field)
    );
    const attritionSalesStaffOptions = Array.from(
        new Map(
            (analytics.salesStaffRanking ?? [])
                .filter((item) => item?.salesStaffId)
                .map((item) => [
                    item.salesStaffId.toString(),
                    {
                        value: item.salesStaffId.toString(),
                        label: item.salesStaff || t('notAssigned')
                    }
                ])
        ).values()
    );
    const selectedAttritionSalesStaff = attritionSalesStaffOptions.find(
        (item) => item.value === selectedAttritionSalesStaffId
    );

    const attritionOverallRate = Number(attritionStats.overallRate ?? 0);
    const attritionResignedCount = Number(attritionStats.resignedCount ?? 0);
    const attritionPopulationCount = Number(attritionStats.populationCount ?? 0);
    const attritionRangeLabelStart = attritionDateFilters.startMonth || '-';
    const attritionRangeLabelEnd = attritionDateFilters.endMonth || '-';
    const attritionSalesStaffLabel = selectedAttritionSalesStaffId === 'all'
        ? t('adminAnalytics.allSalesStaff')
        : (selectedAttritionSalesStaff?.label ?? t('adminAnalytics.allSalesStaff'));

    const buildAttritionChartData = (items, baseColor) => ({
        labels: items.map((item) => item.label),
        datasets: [{
            label: t('adminAnalytics.attritionRate'),
            data: items.map((item) => item.attritionRate),
            resignedCounts: items.map((item) => item.resignedCount),
            totalCounts: items.map((item) => item.totalCount),
            backgroundColor: items.map((_, index) => `${CHART_COLORS[index % CHART_COLORS.length]}CC`),
            borderColor: baseColor,
            borderWidth: 1,
            borderRadius: 0,
            barThickness: 20
        }]
    });

    const attritionNationalityChartData = buildAttritionChartData(attritionByNationality, '#EF4444');
    const attritionFieldChartData = buildAttritionChartData(attritionByField, '#F97316');
    const attritionChartOptions = {
        indexAxis: 'y',
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const dataset = context.dataset;
                        const resigned = dataset.resignedCounts?.[context.dataIndex] ?? 0;
                        const total = dataset.totalCounts?.[context.dataIndex] ?? 0;
                        const rate = context.parsed.x ?? 0;
                        return `${t('adminAnalytics.attritionRate')}: ${rate}% (${resigned}/${total})`;
                    }
                }
            }
        },
        scales: {
            x: {
                beginAtZero: true,
                suggestedMax: 100,
                ticks: {
                    callback: function (value) {
                        return `${value}%`;
                    }
                }
            },
            y: {
                grid: {
                    display: false
                }
            }
        }
    };

    // 1. 折线图数据（根据选择切换）
    const getLineChartData = () => {
        if (chartOptions.lineChart === 'revenue') {
            // 各营业员月度管理费收入趋势
            return {
                labels: analytics.salesStaffMonthlyRevenue.months,
                datasets: analytics.salesStaffMonthlyRevenue.series.map((series, index) => ({
                    label: series.salesStaff,
                    data: series.data,
                    borderColor: CHART_COLORS[index % CHART_COLORS.length],
                    backgroundColor: `${CHART_COLORS[index % CHART_COLORS.length]}33`,
                    tension: 0.3,
                    fill: false,
                    pointRadius: 4,
                    pointHoverRadius: 6
                }))
            };
        } else {
            // 默认：各营业员月度人才数量趋势
            return {
                labels: analytics.salesStaffMonthlyTrend.months,
                datasets: analytics.salesStaffMonthlyTrend.series.map((series, index) => ({
                    label: series.salesStaff,
                    data: series.data,
                    borderColor: CHART_COLORS[index % CHART_COLORS.length],
                    backgroundColor: `${CHART_COLORS[index % CHART_COLORS.length]}33`,
                    tension: 0.3,
                    fill: false,
                    pointRadius: 4,
                    pointHoverRadius: 6
                }))
            };
        }
    };

    const salesStaffTrendData = getLineChartData();

    const salesStaffTrendOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    padding: 15,
                    font: {
                        size: 11
                    }
                }
            },
            title: {
                display: false
            },
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    label: function (context) {
                        if (chartOptions.lineChart === 'revenue') {
                            return `${context.dataset.label}: ${formatCurrency(context.parsed.y)}`;
                        }
                        return `${context.dataset.label}: ${context.parsed.y}人`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: chartOptions.lineChart === 'revenue' ? undefined : 1,
                    callback: function (value) {
                        if (chartOptions.lineChart === 'revenue') {
                            return formatCurrency(value);
                        }
                        return value + '人';
                    }
                }
            },
            x: {
                grid: {
                    display: false
                }
            }
        },
        interaction: {
            mode: 'nearest',
            axis: 'x',
            intersect: false
        }
    };

    // 2. 饼图/堆叠柱状图数据（根据选择切换）
    const getPieChartData = () => {
        if (chartOptions.pieChart === 'field' || chartOptions.pieChart === 'field_stacked') {
            // 分野分布
            const fieldTotal = translatedFieldDistribution.reduce((sum, item) => sum + (item.count ?? 0), 0);
            return {
                labels: translatedFieldDistribution.map(f => f.label),
                datasets: [{
                    data: translatedFieldDistribution.map(f => f.count),
                    backgroundColor: CHART_COLORS,
                    borderWidth: 2,
                    borderColor: '#ffffff',
                    borderRadius: 0
                }],
                total: fieldTotal,
                distribution: translatedFieldDistribution,
                isStacked: chartOptions.pieChart === 'field_stacked'
            };
        } else if (chartOptions.pieChart === 'nationality_stacked') {
            // 国籍分布 - 堆叠柱状图
            return {
                labels: translatedNationalityDistribution.map(n => n.label),
                datasets: [{
                    data: translatedNationalityDistribution.map(n => n.count),
                    backgroundColor: CHART_COLORS,
                    borderWidth: 2,
                    borderColor: '#ffffff',
                    borderRadius: 0
                }],
                total: nationalityTotal,
                distribution: translatedNationalityDistribution,
                isStacked: true
            };
        } else {
            // 默认：国籍分布 - 饼图
            return {
                labels: translatedNationalityDistribution.map(n => n.label),
                datasets: [{
                    data: translatedNationalityDistribution.map(n => n.count),
                    backgroundColor: CHART_COLORS,
                    borderWidth: 2,
                    borderColor: '#ffffff'
                }],
                total: nationalityTotal,
                distribution: translatedNationalityDistribution,
                isStacked: false
            };
        }
    };

    const pieChartData = getPieChartData();
    const nationalityData = pieChartData;

    const nationalityOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'right',
                labels: {
                    usePointStyle: true,
                    padding: 15,
                    font: {
                        size: 11
                    },
                    generateLabels: function (chart) {
                        const dataset = chart.data.datasets[0];
                        const colors = Array.isArray(dataset.backgroundColor) ? dataset.backgroundColor : [dataset.backgroundColor];
                        return pieChartData.distribution.map((item, index) => {
                            const value = dataset.data[index] ?? 0;
                            const color = colors[index % colors.length];
                            const percentage = item?.percentage ?? (pieChartData.total > 0
                                ? Math.round((value / pieChartData.total) * 1000) / 10
                                : 0);
                            const labelText = item?.label || '未設定';
                            return {
                                text: `${labelText}: ${value}人 (${percentage}%)`,
                                fillStyle: color,
                                hidden: false,
                                index
                            };
                        });
                    }
                }
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const label = context.label || '';
                        const value = context.parsed;
                        const dataPoint = pieChartData.distribution[context.dataIndex];
                        const percentage = dataPoint?.percentage ?? (pieChartData.total > 0
                            ? Math.round((value / pieChartData.total) * 1000) / 10
                            : 0);
                        return `${label}: ${value}人 (${percentage}%)`;
                    }
                }
            }
        }
    };

    // 堆叠柱状图配置（用于分布数据的替代显示）
    const stackedBarOptions = {
        indexAxis: 'y', // 水平柱状图，更适合展示分类数据
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false // 隐藏图例，因为每个柱子颜色不同
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        const value = context.parsed.x;
                        const dataPoint = pieChartData.distribution[context.dataIndex];
                        const percentage = dataPoint?.percentage ?? (pieChartData.total > 0
                            ? Math.round((value / pieChartData.total) * 1000) / 10
                            : 0);
                        return `${value}人 (${percentage}%)`;
                    }
                }
            }
        },
        scales: {
            x: {
                beginAtZero: true,
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)'
                },
                ticks: {
                    callback: function (value) {
                        return value + '人';
                    }
                }
            },
            y: {
                grid: {
                    display: false
                },
                ticks: {
                    font: {
                        size: 11
                    }
                }
            }
        }
    };

    // 3. 柱状图1数据（根据选择切换）
    const getBarChart1Data = () => {
        if (chartOptions.barChart1 === 'revenue') {
            // 月度管理费收入
            return {
                labels: analytics.monthlyManagementFeeRevenue.map(m => m.month),
                datasets: [{
                    label: t('adminAnalytics.managementFeeRevenue'),
                    data: analytics.monthlyManagementFeeRevenue.map(m => m.revenue),
                    backgroundColor: '#6366F1',
                    borderRadius: 0,
                    barThickness: 30
                }]
            };
        } else {
            // 默认：月度新增人才
            return {
                labels: analytics.monthlyNewTalents.map(m => m.month),
                datasets: [{
                    label: t('adminAnalytics.newTalents'),
                    data: analytics.monthlyNewTalents.map(m => m.count),
                    backgroundColor: '#6366F1',
                    borderRadius: 0,
                    barThickness: 30
                }]
            };
        }
    };

    const monthlyNewData = getBarChart1Data();

    const monthlyNewOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        if (chartOptions.barChart1 === 'revenue') {
                            return formatCurrency(context.parsed.y);
                        }
                        return `${context.parsed.y}人`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: chartOptions.barChart1 === 'revenue' ? undefined : 1,
                    callback: function (value) {
                        if (chartOptions.barChart1 === 'revenue') {
                            return formatCurrency(value);
                        }
                        return value + '人';
                    }
                }
            },
            x: {
                grid: {
                    display: false
                }
            }
        }
    };

    // 4. 柱状图2数据（根据选择切换）
    const getBarChart2Data = () => {
        if (chartOptions.barChart2 === 'year_over_year') {
            // 历年同期对比
            return {
                labels: analytics.yearOverYearComparison.quarters,
                datasets: analytics.yearOverYearComparison.series.map((series, index) => ({
                    label: `${series.year}年`,
                    data: series.data,
                    backgroundColor: index === 0 ? '#94A3B8' : '#6366F1',
                    borderRadius: 0,
                    barThickness: 40
                }))
            };
        } else {
            // 默认：本年度季度对比
            return {
                labels: analytics.quarterlyStats.map(q => q.quarter),
                datasets: [{
                    label: t('adminAnalytics.quarterlyTalents'),
                    data: analytics.quarterlyStats.map(q => q.count),
                    backgroundColor: ['#6366F1', '#22C55E', '#F97316', '#EC4899'],
                    borderRadius: 0,
                    barThickness: 50
                }]
            };
        }
    };

    const quarterlyData = getBarChart2Data();

    const quarterlyOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: chartOptions.barChart2 === 'year_over_year',
                position: 'bottom'
            },
            tooltip: {
                callbacks: {
                    label: function (context) {
                        if (chartOptions.barChart2 === 'year_over_year') {
                            return `${context.dataset.label}: ${context.parsed.y}人`;
                        }
                        return `${context.parsed.y}人`;
                    }
                }
            }
        },
        scales: {
            y: {
                beginAtZero: true,
                ticks: {
                    stepSize: 1,
                    callback: function (value) {
                        return value + '人';
                    }
                }
            },
            x: {
                grid: {
                    display: false
                }
            }
        }
    };

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('ja-JP', {
            style: 'currency',
            currency: 'JPY',
            minimumFractionDigits: 0
        }).format(value);
    };

    const handleDateFilterChange = (field, value) => {
        setPendingDateFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleClearDateFilters = () => {
        setPendingDateFilters(initialDateFilter);
        setDateFilters(initialDateFilter);
        setDateValidationError(null);
        setManagementFeeView('current');
    };

    const handleApplyDateFilters = () => {
        const { startMonth, endMonth } = pendingDateFilters;
        if (startMonth && endMonth && startMonth > endMonth) {
            setDateValidationError(t('adminAnalytics.invalidDateRange'));
            return;
        }
        setDateValidationError(null);
        setDateFilters(pendingDateFilters);
        const hasRangeFilter = Boolean(startMonth || endMonth);
        if (hasRangeFilter && managementFeeView === 'current') {
            setManagementFeeView('rangeCombined');
        }
    };

    const handleInitialCostDateFilterChange = (field, value) => {
        setPendingInitialCostDateFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleClearInitialCostDateFilters = () => {
        setPendingInitialCostDateFilters(initialDateFilter);
        setInitialCostDateFilters(initialDateFilter);
        setInitialCostDateValidationError(null);
    };

    const handleApplyInitialCostDateFilters = () => {
        const { startMonth, endMonth } = pendingInitialCostDateFilters;
        if (startMonth && endMonth && startMonth > endMonth) {
            setInitialCostDateValidationError(t('adminAnalytics.invalidDateRange'));
            return;
        }
        setInitialCostDateValidationError(null);
        setInitialCostDateFilters(pendingInitialCostDateFilters);
    };

    const handleAttritionDateFilterChange = (field, value) => {
        setPendingAttritionDateFilters(prev => ({ ...prev, [field]: value }));
    };

    const handleClearAttritionDateFilters = () => {
        setPendingAttritionDateFilters(initialDateFilter);
        setAttritionDateFilters(initialDateFilter);
        setAttritionDateValidationError(null);
    };

    const handleApplyAttritionDateFilters = () => {
        const { startMonth, endMonth } = pendingAttritionDateFilters;
        if (startMonth && endMonth && startMonth > endMonth) {
            setAttritionDateValidationError(t('adminAnalytics.invalidDateRange'));
            return;
        }
        setAttritionDateValidationError(null);
        setAttritionDateFilters(pendingAttritionDateFilters);
    };

    const initialCostPendingFilters = syncInitialCostFilters ? pendingDateFilters : pendingInitialCostDateFilters;
    const appliedInitialCostFilters = syncInitialCostFilters ? dateFilters : initialCostDateFilters;

    const handleInitialCostTalentCountClick = () => {
        if (typeof onInitialCostTalentCountClick !== 'function') {
            return;
        }

        onInitialCostTalentCountClick({
            selectedSalesStaffId: initialCostSelectedId,
            startMonth: appliedInitialCostFilters.startMonth || '',
            endMonth: appliedInitialCostFilters.endMonth || ''
        });
    };

    return (
        <div className="space-y-6">
            {/* 営業担当者ランキング */}
            <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">
                        {t('adminAnalytics.salesStaffRanking')}
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        {t('adminAnalytics.salesStaffRankingSubtitle')}
                    </p>
                </div>
                <div className="overflow-x-auto">
                    {(() => {
                        const totals = salesStaffRanking.reduce((acc, staff) => ({
                            employedTalents: acc.employedTalents + (staff.employedTalents || 0),
                            pendingTalents: acc.pendingTalents + (staff.pendingTalents || 0),
                            applyingTalents: acc.applyingTalents + (staff.applyingTalents || 0),
                            childcareLeaveTalents: acc.childcareLeaveTalents + (staff.childcareLeaveTalents || 0),
                            awaitingEntryTalents: acc.awaitingEntryTalents + (staff.awaitingEntryTalents || 0),
                            totalTalents: acc.totalTalents + (staff.totalTalents || 0)
                        }), {
                            employedTalents: 0,
                            pendingTalents: 0,
                            applyingTalents: 0,
                            childcareLeaveTalents: 0,
                            awaitingEntryTalents: 0,
                            totalTalents: 0
                        });
                        return (
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('adminAnalytics.rank')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('adminAnalytics.salesStaffName')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('adminAnalytics.employedCount')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('adminAnalytics.pendingCount')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('adminAnalytics.applyingCount')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('adminAnalytics.childcareLeaveCount')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('adminAnalytics.awaitingEntryCount')}
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            {t('adminAnalytics.totalTalentsCount')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {salesStaffRanking.map((staff, index) => (
                                        <tr key={staff.salesStaff} className={index < 3 ? 'bg-yellow-50' : ''}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${index === 0 ? 'bg-yellow-400 text-yellow-900' :
                                                    index === 1 ? 'bg-gray-300 text-gray-700' :
                                                        index === 2 ? 'bg-orange-300 text-orange-900' :
                                                            'bg-gray-100 text-gray-600'
                                                    }`}>
                                                    {index + 1}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {staff.salesStaff}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                                                {t('adminAnalytics.peopleCount', { count: staff.employedTalents })}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600">
                                                {t('adminAnalytics.peopleCount', { count: staff.pendingTalents })}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-yellow-600">
                                                {t('adminAnalytics.peopleCount', { count: staff.applyingTalents })}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-purple-600">
                                                {t('adminAnalytics.peopleCount', { count: staff.childcareLeaveTalents })}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-indigo-600">
                                                {t('adminAnalytics.peopleCount', { count: staff.awaitingEntryTalents })}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-indigo-600">
                                                {t('adminAnalytics.peopleCount', { count: staff.totalTalents })}
                                            </td>
                                        </tr>
                                    ))}
                                    <tr className="bg-gray-100">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-700">
                                            合計
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-gray-900">
                                            合計
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-700">
                                            {t('adminAnalytics.peopleCount', { count: totals.employedTalents })}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-blue-700">
                                            {t('adminAnalytics.peopleCount', { count: totals.pendingTalents })}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-yellow-700">
                                            {t('adminAnalytics.peopleCount', { count: totals.applyingTalents })}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-purple-700">
                                            {t('adminAnalytics.peopleCount', { count: totals.childcareLeaveTalents })}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-indigo-700">
                                            {t('adminAnalytics.peopleCount', { count: totals.awaitingEntryTalents })}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-indigo-700">
                                            {t('adminAnalytics.peopleCount', { count: totals.totalTalents })}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        );
                    })()}
                </div>
            </div>

            {/* 離職率分析 */}
            <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                            {t('adminAnalytics.attritionAnalysis')}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            {t('adminAnalytics.attritionAnalysisSubtitle')}
                        </p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-3">
                        <div>
                            <label className="text-xs text-gray-600 mb-1 block">{t('adminAnalytics.salesStaffName')}</label>
                            <select
                                value={selectedAttritionSalesStaffId}
                                onChange={(event) => setSelectedAttritionSalesStaffId(event.target.value)}
                                className="w-full rounded-md border border-rose-200 px-3 py-2 text-sm text-gray-700 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-400 bg-white"
                            >
                                <option value="all">{t('adminAnalytics.allSalesStaff')}</option>
                                {attritionSalesStaffOptions.map((item) => (
                                    <option key={item.value} value={item.value}>
                                        {item.label}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-gray-600 mb-1 block">{t('adminAnalytics.startMonth')}</label>
                            <input
                                type="month"
                                value={pendingAttritionDateFilters.startMonth}
                                onChange={(e) => handleAttritionDateFilterChange('startMonth', e.target.value)}
                                className="w-full rounded-md border border-rose-200 px-3 py-2 text-sm text-gray-700 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-400 bg-white"
                            />
                        </div>
                        <div>
                            <label className="text-xs text-gray-600 mb-1 block">{t('adminAnalytics.endMonth')}</label>
                            <input
                                type="month"
                                value={pendingAttritionDateFilters.endMonth}
                                onChange={(e) => handleAttritionDateFilterChange('endMonth', e.target.value)}
                                className="w-full rounded-md border border-rose-200 px-3 py-2 text-sm text-gray-700 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-400 bg-white"
                            />
                        </div>
                        <div className="flex items-end">
                            <button
                                type="button"
                                onClick={handleApplyAttritionDateFilters}
                                className="w-full rounded-md bg-rose-600 text-white px-3 py-2 text-sm hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500"
                            >
                                {t('adminAnalytics.applyDateFilters')}
                            </button>
                        </div>
                        <div className="flex items-end">
                            <button
                                type="button"
                                onClick={handleClearAttritionDateFilters}
                                className="w-full rounded-md border border-rose-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-rose-50 focus:border-rose-500 focus:outline-none focus:ring-2 focus:ring-rose-400"
                            >
                                {t('adminAnalytics.clearDateFilters')}
                            </button>
                        </div>
                    </div>
                </div>
                {attritionDateValidationError && (
                    <p className="mt-2 text-xs text-red-600">{attritionDateValidationError}</p>
                )}
                <div className="mt-3 flex flex-wrap items-center gap-4 text-sm text-gray-600">
                    <span>
                        {t('adminAnalytics.attritionSalesStaffSummary', {
                            salesStaff: attritionSalesStaffLabel
                        })}
                    </span>
                    <span>
                        {t('adminAnalytics.attritionDateRangeSummary', {
                            start: attritionRangeLabelStart,
                            end: attritionRangeLabelEnd
                        })}
                    </span>
                    <span>
                        {t('adminAnalytics.attritionSummary', {
                            rate: attritionOverallRate,
                            resigned: attritionResignedCount,
                            total: attritionPopulationCount
                        })}
                    </span>
                </div>
                <div className="grid lg:grid-cols-2 gap-6 mt-4">
                    <div className="bg-rose-50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-rose-900 mb-3">{t('adminAnalytics.attritionRateByNationality')}</h4>
                        {attritionByNationality.length > 0 ? (
                            <div style={{ height: '320px' }}>
                                <Bar data={attritionNationalityChartData} options={attritionChartOptions} />
                            </div>
                        ) : (
                            <div className="h-40 flex items-center justify-center text-sm text-gray-500">
                                {t('adminAnalytics.attritionNoData')}
                            </div>
                        )}
                    </div>
                    <div className="bg-orange-50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-orange-900 mb-3">{t('adminAnalytics.attritionRateByField')}</h4>
                        {attritionByField.length > 0 ? (
                            <div style={{ height: '320px' }}>
                                <Bar data={attritionFieldChartData} options={attritionChartOptions} />
                            </div>
                        ) : (
                            <div className="h-40 flex items-center justify-center text-sm text-gray-500">
                                {t('adminAnalytics.attritionNoData')}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* 特定技能2号統計カード */}
            <div className="bg-gradient-to-br from-cyan-50 to-indigo-50 rounded-xl shadow-lg p-6">
                <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
                    <div>
                        <h3 className="text-lg font-semibold text-indigo-900">特定技能2号統計</h3>
                        <p className="text-sm text-indigo-700 mt-1">現在の在留資格が特定技能2号の人材は、通常統計から分離しています。</p>
                    </div>
                    <div className="text-sm text-indigo-700">
                        未登録紹介費: {specifiedSkill2Stats.unregisteredFeeCount ?? 0}人
                    </div>
                </div>
                <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">人数</div>
                        <div className="text-2xl font-bold text-indigo-600">{specifiedSkill2Stats.totalCount ?? 0}人</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">紹介費合計</div>
                        <div className="text-2xl font-bold text-cyan-700">{formatCurrency(specifiedSkill2Stats.totalIntroductionFee ?? 0)}</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">平均紹介費</div>
                        <div className="text-2xl font-bold text-emerald-600">{formatCurrency(specifiedSkill2Stats.averageIntroductionFee ?? 0)}</div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">営業担当数</div>
                        <div className="text-2xl font-bold text-slate-700">{(specifiedSkill2Stats.bySalesStaff ?? []).length}名</div>
                    </div>
                </div>
                {(specifiedSkill2Stats.bySalesStaff ?? []).length > 0 && (
                    <div className="mt-4 overflow-x-auto bg-white rounded-lg shadow-sm">
                        <table className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left font-medium text-gray-500">営業担当</th>
                                    <th className="px-4 py-3 text-right font-medium text-gray-500">人数</th>
                                    <th className="px-4 py-3 text-right font-medium text-gray-500">紹介費合計</th>
                                    <th className="px-4 py-3 text-right font-medium text-gray-500">未登録</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {(specifiedSkill2Stats.bySalesStaff ?? []).map(item => (
                                    <tr key={item.salesStaffId || item.salesStaff}>
                                        <td className="px-4 py-3 font-medium text-gray-900">{item.salesStaff}</td>
                                        <td className="px-4 py-3 text-right text-gray-700">{item.talentCount ?? 0}人</td>
                                        <td className="px-4 py-3 text-right text-cyan-700 font-semibold">{formatCurrency(item.totalIntroductionFee ?? 0)}</td>
                                        <td className="px-4 py-3 text-right text-gray-500">{item.unregisteredFeeCount ?? 0}人</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* 初期費用統計カード */}
            <div className="bg-gradient-to-br from-amber-50 to-orange-50 rounded-xl shadow-lg p-6">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <h3 className="text-lg font-semibold text-orange-900">
                        {t('adminAnalytics.initialCostStats')}
                    </h3>
                    <div className="flex flex-col gap-3">
                        <div className="flex items-center gap-2">
                            <input
                                id="initial-cost-sync"
                                type="checkbox"
                                checked={syncInitialCostFilters}
                                onChange={(event) => setSyncInitialCostFilters(event.target.checked)}
                                className="h-4 w-4 rounded border-gray-300 text-orange-600 focus:ring-orange-500"
                            />
                            <label htmlFor="initial-cost-sync" className="text-xs text-gray-600">
                                {t('adminAnalytics.initialCostSyncLabel')}
                            </label>
                        </div>
                        <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                            <label htmlFor="initial-cost-filter" className="text-sm text-gray-600">
                                {t('adminAnalytics.managementFeeFilterLabel')}
                            </label>
                            <select
                                id="initial-cost-filter"
                                value={initialCostSelectedId}
                                onChange={(event) => setSelectedInitialCostSalesStaffId(event.target.value)}
                                disabled={syncInitialCostFilters}
                                className={`mt-1 sm:mt-0 rounded-md border border-orange-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-400 ${syncInitialCostFilters ? 'opacity-60' : ''}`}
                            >
                                <option value="all">{t('adminAnalytics.allSalesStaff')}</option>
                                {initialCostBySalesStaff.map((item) => {
                                    const value = getSalesStaffOptionValue(item);
                                    return (
                                        <option key={value} value={value}>
                                            {item.salesStaff} ({t('adminAnalytics.peopleCount', { count: item.talentCount ?? 0 })})
                                        </option>
                                    );
                                })}
                            </select>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">{t('adminAnalytics.startMonth')}</label>
                                <input
                                    type="month"
                                    value={initialCostPendingFilters.startMonth}
                                    onChange={(e) => handleInitialCostDateFilterChange('startMonth', e.target.value)}
                                    disabled={syncInitialCostFilters}
                                    className={`w-full rounded-md border border-orange-200 px-3 py-2 text-sm text-gray-700 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white ${syncInitialCostFilters ? 'opacity-60' : ''}`}
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">{t('adminAnalytics.endMonth')}</label>
                                <input
                                    type="month"
                                    value={initialCostPendingFilters.endMonth}
                                    onChange={(e) => handleInitialCostDateFilterChange('endMonth', e.target.value)}
                                    disabled={syncInitialCostFilters}
                                    className={`w-full rounded-md border border-orange-200 px-3 py-2 text-sm text-gray-700 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white ${syncInitialCostFilters ? 'opacity-60' : ''}`}
                                />
                            </div>
                            <div className="flex items-end">
                                <button
                                    type="button"
                                    onClick={handleApplyInitialCostDateFilters}
                                    disabled={syncInitialCostFilters}
                                    className={`w-full rounded-md bg-orange-600 text-white px-3 py-2 text-sm hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-orange-500 ${syncInitialCostFilters ? 'opacity-60 cursor-not-allowed' : ''}`}
                                >
                                    {t('adminAnalytics.applyDateFilters')}
                                </button>
                            </div>
                            <div className="flex items-end">
                                <button
                                    type="button"
                                    onClick={handleClearInitialCostDateFilters}
                                    disabled={syncInitialCostFilters}
                                    className={`w-full rounded-md border border-orange-200 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-orange-50 focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-400 ${syncInitialCostFilters ? 'opacity-60 cursor-not-allowed' : ''}`}
                                >
                                    {t('adminAnalytics.clearDateFilters')}
                                </button>
                            </div>
                        </div>
                        {!syncInitialCostFilters && initialCostDateValidationError && (
                            <p className="text-xs text-red-600">{initialCostDateValidationError}</p>
                        )}
                    </div>
                </div>
                <p className="mt-3 text-sm text-gray-500">
                    {t('adminAnalytics.managementFeeCurrentView', {
                        salesStaff: syncInitialCostFilters ? currentSalesStaffLabel : currentInitialCostLabel,
                        count: currentInitialCostCount
                    })}
                </p>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-4">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('adminAnalytics.totalInitialCost')}</div>
                        <div className="text-2xl font-bold text-orange-600">
                            {formatCurrency(currentInitialCost.totalInitialCost)}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('adminAnalytics.averageInitialCost')}</div>
                        <div className="text-2xl font-bold text-green-600">
                            {formatCurrency(currentInitialCost.averageInitialCost)}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('adminAnalytics.medianInitialCost')}</div>
                        <div className="text-2xl font-bold text-sky-600">
                            {formatCurrency(currentInitialCost.medianInitialCost ?? 0)}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('adminAnalytics.maxInitialCost')}</div>
                        <div className="text-2xl font-bold text-red-500">
                            {formatCurrency(currentInitialCost.maxCost)}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('adminAnalytics.minInitialCost')}</div>
                        <div className="text-2xl font-bold text-sky-600">
                            {formatCurrency(currentInitialCost.minCost)}
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={handleInitialCostTalentCountClick}
                        className="bg-white rounded-lg p-4 shadow-sm text-left transition hover:shadow-md hover:ring-2 hover:ring-indigo-200 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                        <div className="text-sm text-gray-600 mb-1">{t('adminAnalytics.initialCostTalentCount')}</div>
                        <div className="text-2xl font-bold text-indigo-600">
                            {currentInitialCostCount}人
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {t('adminAnalytics.initialCostCoverageRate', { rate: currentInitialCost.coverageRate ?? 0 })}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {t('adminAnalytics.initialCostChargeablePopulation', { count: currentInitialCost.chargeablePopulation ?? 0 })}
                        </div>
                        <div className="text-xs text-gray-500 mt-1">
                            {t('adminAnalytics.initialCostNoChargeCount', { count: currentInitialCost.noChargeTalents ?? 0 })}
                        </div>
                    </button>
                </div>
            </div>

            {/* 管理费统计卡片 */}
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl shadow-lg p-6">
                <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <h3 className="text-lg font-semibold text-indigo-900">
                        {t('adminAnalytics.managementFeeStats')}
                    </h3>
                    <div className="flex flex-col gap-3">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                                <label htmlFor="management-fee-filter" className="text-sm text-gray-600 whitespace-nowrap">
                                    {t('adminAnalytics.managementFeeFilterLabel')}
                                </label>
                                <select
                                    id="management-fee-filter"
                                    value={selectedSalesStaffId}
                                    onChange={(event) => setSelectedSalesStaffId(event.target.value)}
                                    className="mt-1 sm:mt-0 rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                >
                                    <option value="all">{t('adminAnalytics.allSalesStaff')}</option>
                                    {managementFeeBySalesStaff.map((item) => {
                                        const value = getSalesStaffOptionValue(item);
                                        const optionTalentCount = managementFeeView === 'current'
                                            ? getCurrentEmployedTalentCount(item)
                                            : (item.views?.[managementFeeView]?.targetTalentsCount ?? item.rangeTargetTalentsCount ?? item.rangeTalentsWithFee ?? item.talentCount ?? 0);
                                        return (
                                            <option key={value} value={value}>
                                                {item.salesStaff} ({t('adminAnalytics.peopleCount', { count: optionTalentCount })})
                                            </option>
                                        );
                                    })}
                                </select>
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:gap-2">
                                <label htmlFor="management-fee-view" className="text-sm text-gray-600 whitespace-nowrap">
                                    {t('adminAnalytics.managementFeeViewLabel')}
                                </label>
                                <select
                                    id="management-fee-view"
                                    value={managementFeeView}
                                    onChange={(event) => setManagementFeeView(event.target.value)}
                                    className="mt-1 sm:mt-0 rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                                >
                                    <option value="current">{t('adminAnalytics.managementFeeViewOption.current')}</option>
                                    <option value="rangeActual">{t('adminAnalytics.managementFeeViewOption.rangeActual')}</option>
                                    <option value="rangeForecast">{t('adminAnalytics.managementFeeViewOption.rangeForecast')}</option>
                                    <option value="rangeCombined">{t('adminAnalytics.managementFeeViewOption.rangeCombined')}</option>
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">{t('adminAnalytics.startMonth')}</label>
                                <input
                                    type="month"
                                    value={pendingDateFilters.startMonth}
                                    onChange={(e) => handleDateFilterChange('startMonth', e.target.value)}
                                    disabled={!isRangeView}
                                    className={`w-full rounded-md border border-indigo-200 px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400 ${isRangeView ? 'bg-white' : 'bg-gray-100 opacity-60 cursor-not-allowed'}`}
                                />
                            </div>
                            <div>
                                <label className="text-xs text-gray-600 mb-1 block">{t('adminAnalytics.endMonth')}</label>
                                <input
                                    type="month"
                                    value={pendingDateFilters.endMonth}
                                    onChange={(e) => handleDateFilterChange('endMonth', e.target.value)}
                                    disabled={!isRangeView}
                                    className={`w-full rounded-md border border-indigo-200 px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400 ${isRangeView ? 'bg-white' : 'bg-gray-100 opacity-60 cursor-not-allowed'}`}
                                />
                            </div>
                            <div className="flex items-end">
                                <button
                                    type="button"
                                    onClick={handleApplyDateFilters}
                                    disabled={!isRangeView}
                                    className={`w-full rounded-md text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${isRangeView ? 'bg-indigo-600 hover:bg-indigo-700' : 'bg-indigo-300 cursor-not-allowed'}`}
                                >
                                    {t('adminAnalytics.applyDateFilters')}
                                </button>
                            </div>
                            <div className="flex items-end">
                                <button
                                    type="button"
                                    onClick={handleClearDateFilters}
                                    disabled={!isRangeView}
                                    className={`w-full rounded-md border border-indigo-200 px-3 py-2 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400 ${isRangeView ? 'bg-white hover:bg-indigo-50' : 'bg-gray-100 opacity-60 cursor-not-allowed'}`}
                                >
                                    {t('adminAnalytics.clearDateFilters')}
                                </button>
                            </div>
                        </div>
                        {!isRangeView && (
                            <p className="text-xs text-gray-500">{t('adminAnalytics.managementFeeCurrentRangeDisabledHint')}</p>
                        )}
                        {isRangeView && dateValidationError && (
                            <p className="text-xs text-red-600">{dateValidationError}</p>
                        )}
                    </div>
                </div>
                <p className="mt-3 text-sm text-gray-500">
                    {t(managementFeeCurrentViewLabelKey, {
                        salesStaff: currentSalesStaffLabel,
                        count: currentTalentCount
                    })}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                    {currentFeeCountLabel}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                    {t('adminAnalytics.managementFeeNoChargeCount', { count: currentNoChargeTalentCount })}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                    {t('adminAnalytics.managementFeeOtherCount', { count: currentNonBilledTalentCount })}
                </p>
                <p className="mt-1 text-xs text-gray-500">
                    {t('adminAnalytics.managementFeeViewRange', {
                        start: currentManagementFeeView.startMonth || '-',
                        end: currentManagementFeeView.endMonth || '-'
                    })}
                </p>
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{managementFeeTotalLabel}</div>
                        <div className="text-2xl font-bold text-indigo-600">
                            {formatCurrency(currentManagementFeeView.totalFee ?? 0)}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('adminAnalytics.averageFeePerTalent')}</div>
                        <div className="text-2xl font-bold text-green-600">
                            {formatCurrency(currentManagementFeeView.averageFeePerTalent ?? 0)}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('adminAnalytics.medianFee')}</div>
                        <div className="text-2xl font-bold text-sky-600">
                            {formatCurrency(currentManagementFeeView.medianFee ?? 0)}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('adminAnalytics.maxFee')}</div>
                        <div className="text-2xl font-bold text-orange-600">
                            {formatCurrency(currentManagementFeeView.maxFee ?? 0)}
                        </div>
                    </div>
                    <div className="bg-white rounded-lg p-4 shadow-sm">
                        <div className="text-sm text-gray-600 mb-1">{t('adminAnalytics.minFee')}</div>
                        <div className="text-2xl font-bold text-slate-600">
                            {formatCurrency(currentManagementFeeView.minFee ?? 0)}
                        </div>
                    </div>
                    {managementFeeView === 'current' ? (
                        <>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <div className="text-sm text-gray-600 mb-1">{t('adminAnalytics.projectedAnnualRevenue')}</div>
                                <div className="text-2xl font-bold text-purple-600">
                                    {formatCurrency(currentManagementFee.projectedAnnualRevenue ?? 0)}
                                </div>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <div className="text-sm text-gray-600 mb-1">{t('adminAnalytics.firstMonthFeeTotal')}</div>
                                <div className="text-2xl font-bold text-pink-600">
                                    {formatCurrency(currentManagementFee.firstMonthFeeTotal ?? 0)}
                                </div>
                                <div className="text-xs text-gray-500 mt-1">
                                    {t('adminAnalytics.firstMonthFeeCount', { count: currentManagementFee.firstMonthFeeCount ?? 0 })}
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <div className="text-sm text-gray-600 mb-1">{t('adminAnalytics.billedMonths')}</div>
                                <div className="text-2xl font-bold text-purple-600">
                                    {currentManagementFeeView.billedMonths ?? 0}{t('adminAnalytics.monthsUnit')}
                                </div>
                            </div>
                            <div className="bg-white rounded-lg p-4 shadow-sm">
                                <div className="text-sm text-gray-600 mb-1">{t('adminAnalytics.pausedMonths')}</div>
                                <div className="text-2xl font-bold text-pink-600">
                                    {currentManagementFeeView.pausedMonths ?? 0}{t('adminAnalytics.monthsUnit')}
                                </div>
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* 折线图：营业员月度趋势 */}
            <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="mb-4 flex justify-between items-start">
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                            {chartOptions.lineChart === 'revenue'
                                ? t('adminAnalytics.salesStaffRevenueTitle')
                                : t('adminAnalytics.salesStaffTrend')}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            {chartOptions.lineChart === 'revenue'
                                ? t('adminAnalytics.salesStaffRevenueSubtitle')
                                : t('adminAnalytics.salesStaffTrendSubtitle')}
                        </p>
                    </div>
                    <select
                        value={chartOptions.lineChart}
                        onChange={(e) => updateChartOption('lineChart', e.target.value)}
                        className="ml-4 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                        <option value="talent_count">{t('adminAnalytics.chartOption.talentCount')}</option>
                        <option value="revenue">{t('adminAnalytics.chartOption.revenue')}</option>
                    </select>
                </div>
                <div style={{ height: '400px' }}>
                    <Line data={salesStaffTrendData} options={salesStaffTrendOptions} />
                </div>
            </div>

            {/* 饼图和柱状图1 */}
            <div className="grid lg:grid-cols-2 gap-6">
                {/* 饼图/堆叠柱状图：国籍/分野分布 */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="mb-4 flex justify-between items-start">
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {chartOptions.pieChart.includes('field')
                                    ? t('adminAnalytics.fieldDistribution')
                                    : t('adminAnalytics.nationalityDistribution')}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                                {chartOptions.pieChart.includes('field')
                                    ? t('adminAnalytics.fieldDistributionSubtitle')
                                    : t('adminAnalytics.nationalityDistributionSubtitle')}
                                {pieChartData.isStacked && (
                                    <span className="ml-2 text-indigo-600">
                                        ({t('adminAnalytics.chartOption.stackedBarMode') || 'バーグラフモード'})
                                    </span>
                                )}
                            </p>
                        </div>
                        <select
                            value={chartOptions.pieChart}
                            onChange={(e) => updateChartOption('pieChart', e.target.value)}
                            className="ml-4 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        >
                            <optgroup label={t('adminAnalytics.chartOption.pieChart') || '円グラフ'}>
                                <option value="nationality">{t('adminAnalytics.chartOption.nationality')}</option>
                                <option value="field">{t('adminAnalytics.chartOption.field')}</option>
                            </optgroup>
                            <optgroup label={t('adminAnalytics.chartOption.stackedBar') || 'バーグラフ'}>
                                <option value="nationality_stacked">{t('adminAnalytics.chartOption.nationality')} ({t('adminAnalytics.chartOption.bar') || 'バー'})</option>
                                <option value="field_stacked">{t('adminAnalytics.chartOption.field')} ({t('adminAnalytics.chartOption.bar') || 'バー'})</option>
                            </optgroup>
                        </select>
                    </div>
                    <div style={{ height: '300px' }}>
                        {pieChartData.isStacked ? (
                            <Bar data={nationalityData} options={stackedBarOptions} />
                        ) : (
                            <Pie data={nationalityData} options={nationalityOptions} />
                        )}
                    </div>
                </div>

                {/* 柱状图1：月度新增/月度收入 */}
                <div className="bg-white rounded-xl shadow-lg p-6">
                    <div className="mb-4 flex justify-between items-start">
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900">
                                {chartOptions.barChart1 === 'revenue'
                                    ? t('adminAnalytics.monthlyRevenue')
                                    : t('adminAnalytics.monthlyNewTalents')}
                            </h3>
                            <p className="text-sm text-gray-500 mt-1">
                                {chartOptions.barChart1 === 'revenue'
                                    ? t('adminAnalytics.monthlyRevenueSubtitle')
                                    : t('adminAnalytics.monthlyNewTalentsSubtitle')}
                            </p>
                        </div>
                        <select
                            value={chartOptions.barChart1}
                            onChange={(e) => updateChartOption('barChart1', e.target.value)}
                            className="ml-4 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                        >
                            <option value="new_talents">{t('adminAnalytics.chartOption.newTalents')}</option>
                            <option value="revenue">{t('adminAnalytics.chartOption.monthlyRevenue')}</option>
                        </select>
                    </div>
                    <div style={{ height: '300px' }}>
                        <Bar data={monthlyNewData} options={monthlyNewOptions} />
                    </div>
                </div>
            </div>

            {/* 柱状图2：季度对比/历年同期对比 */}
            <div className="bg-white rounded-xl shadow-lg p-6">
                <div className="mb-4 flex justify-between items-start">
                    <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900">
                            {chartOptions.barChart2 === 'year_over_year'
                                ? t('adminAnalytics.yearOverYearComparison')
                                : t('adminAnalytics.quarterlyStats')}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">
                            {chartOptions.barChart2 === 'year_over_year'
                                ? t('adminAnalytics.yearOverYearComparisonSubtitle')
                                : t('adminAnalytics.quarterlyStatsSubtitle', { year: new Date().getFullYear() })}
                        </p>
                    </div>
                    <select
                        value={chartOptions.barChart2}
                        onChange={(e) => updateChartOption('barChart2', e.target.value)}
                        className="ml-4 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                        <option value="quarterly">{t('adminAnalytics.chartOption.quarterly')}</option>
                        <option value="year_over_year">{t('adminAnalytics.chartOption.yearOverYear')}</option>
                    </select>
                </div>
                <div style={{ height: '300px' }}>
                    <Bar data={quarterlyData} options={quarterlyOptions} />
                </div>
            </div>

        </div>
    );
}
